import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ArrowLeft, ChevronLeft, GlassWater, ChevronRight, CloudOff, Check, Maximize, Minus, Pause, Play, Plus, SlidersHorizontal, X } from 'lucide-react';
import type { AttendanceRow, AttendanceStatus, SessionAttendance, SessionDetail, SessionPlayerView } from '@hoopify/shared';
import { api, ApiError, errorMessage } from '../lib/api';
import { formatLongDay, formatTime } from '../lib/dates';
import { AttendanceList, attendanceSummary } from '../components/roster/AttendanceList';
import { DiagramView } from '../components/diagram/DiagramView';
import * as C from '../lib/courtside';
import { uses24Hour } from '../lib/time';

/**
 * Courtside mode: roll call, then the session one drill at a time with a big
 * countdown. Everything is kept on this device, so it carries on without a
 * connection and syncs attendance when it's back.
 */

interface Saved {
  session: SessionDetail;
  rows: AttendanceRow[];
  canMark: boolean;
  run: C.Run;
  /** Attendance marks not yet saved, by player id. */
  pending: Record<string, AttendanceStatus | null>;
  /** Set once timing changes are saved to the plan (or dismissed). */
  planSaved?: boolean;
}

const storageKey = (id: string) => `hoopify.courtside.${id}`;
function load(id: string): Saved | null {
  try {
    const raw = localStorage.getItem(storageKey(id));
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}
function store(s: Saved) {
  try {
    localStorage.setItem(storageKey(s.session.id), JSON.stringify(s));
  } catch {
    /* Private mode or full: carries on in memory. */
  }
}
function forget(id: string) {
  try {
    localStorage.removeItem(storageKey(id));
  } catch {
    /* ignore */
  }
}

function withPending(rows: AttendanceRow[], pending: Saved['pending']) {
  return rows.map((r) => (r.player.id in pending ? { ...r, status: pending[r.player.id]! } : r));
}

export function CourtsidePage() {
  const { teamId = '', sessionId = '' } = useParams();
  const [state, setState] = useState<Saved | null>(() => load(sessionId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);

  const update = useCallback((fn: (s: Saved) => Saved) => {
    setState((s) => {
      if (!s) return s;
      const n = fn(s);
      store(n);
      return n;
    });
  }, []);

  // Fresh copy from the server when we can get one; the saved copy otherwise.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [session, att] = await Promise.all([
          api.get<SessionDetail | SessionPlayerView>(`/teams/${teamId}/sessions/${sessionId}`),
          api.get<SessionAttendance>(`/teams/${teamId}/sessions/${sessionId}/attendance`),
        ]);
        if (!live) return;
        if (session.view !== 'full' || !session.canEdit) {
          setLoadError('Courtside mode is for coaches who can edit this session.');
          return;
        }
        setState((prev) => {
          const run = prev && prev.run.startedAt ? prev.run : C.newRun(session.id, session.version, session.plan);
          const pending = prev?.pending ?? {};
          const n: Saved = { session, rows: withPending(att.rows, pending), canMark: att.canMark, run, pending, planSaved: prev?.planSaved };
          store(n);
          return n;
        });
        setOffline(false);
      } catch (e) {
        if (!live) return;
        if (e instanceof ApiError && e.status === 0) setOffline(true);
        else setLoadError(errorMessage(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [teamId, sessionId]);

  // Attendance sync: send what's pending now, when back online, and every 15 s.
  const stateRef = useRef(state);
  stateRef.current = state;
  const syncing = useRef(false);
  const flush = useCallback(async () => {
    const s = stateRef.current;
    if (!s || syncing.current || !Object.keys(s.pending).length) return;
    syncing.current = true;
    const sent = { ...s.pending };
    try {
      const res = await api.put<SessionAttendance>(`/teams/${teamId}/sessions/${sessionId}/attendance`, {
        marks: Object.entries(sent).map(([playerId, status]) => ({ playerId, status })),
      });
      setOffline(false);
      update((cur) => {
        const pending = { ...cur.pending };
        for (const [id, st] of Object.entries(sent)) if (pending[id] === st) delete pending[id];
        return { ...cur, pending, rows: withPending(res.rows, pending) };
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) setOffline(true);
      else if (e instanceof ApiError && e.status === 400) update((cur) => ({ ...cur, pending: {} })); // A player was removed; drop the stale marks.
    } finally {
      syncing.current = false;
    }
  }, [teamId, sessionId, update]);
  const pendingCount = state ? Object.keys(state.pending).length : 0;
  useEffect(() => {
    if (pendingCount) void flush();
  }, [pendingCount, flush]);
  useEffect(() => {
    const on = () => {
      setOffline(false);
      void flush();
    };
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const t = window.setInterval(() => void flush(), 15_000);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      window.clearInterval(t);
    };
  }, [flush]);

  const mark = (playerId: string, status: AttendanceStatus | null) =>
    update((s) => {
      const pending = { ...s.pending, [playerId]: status };
      return { ...s, pending, rows: withPending(s.rows, pending) };
    });

  if (!state) {
    return (
      <Shell>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          {loadError ? (
            <>
              <p className="mb-6 text-[18px]">{loadError}</p>
              <Link to={`/teams/${teamId}/sessions/${sessionId}`} className="rounded-lg bg-white/10 px-4 py-3">Back to the session</Link>
            </>
          ) : offline ? (
            <>
              <p className="mb-2 text-[18px]">You’re offline.</p>
              <p className="mb-6 text-white/70">Open courtside mode once while you have a connection; after that it keeps working without one.</p>
              <Link to={`/teams/${teamId}/sessions/${sessionId}`} className="rounded-lg bg-white/10 px-4 py-3">Back to the session</Link>
            </>
          ) : (
            <p className="text-white/70">Getting the session ready…</p>
          )}
        </div>
      </Shell>
    );
  }
  if (loadError) {
    return (
      <Shell>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <p className="mb-6 text-[18px]">{loadError}</p>
          <Link to={`/teams/${teamId}/sessions/${sessionId}`} className="rounded-lg bg-white/10 px-4 py-3">Back to the session</Link>
        </div>
      </Shell>
    );
  }

  const sync = <SyncStatus offline={offline} pending={pendingCount} />;
  const { run } = state;
  if (!run.startedAt) return <RollCall state={state} onMark={mark} sync={sync} onStart={() => update((s) => ({ ...s, run: C.start(s.run, Date.now()) }))} />;
  if (run.finishedAt) return <Finished state={state} update={update} sync={sync} offline={offline} />;
  return <Running state={state} update={update} sync={sync} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div data-theme="dark" className="min-h-dvh bg-paper text-ink [color-scheme:dark] [&_h1]:text-ink [&_h2]:text-ink">
      {children}
    </div>
  );
}

function TopBar({ session, children }: { session: SessionDetail; children?: React.ReactNode }) {
  return (
    <header className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
      <Link to={`/teams/${session.teamId}/sessions/${session.id}`} className="-ml-1 rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white" aria-label="Leave courtside mode">
        <ArrowLeft className="size-5" aria-hidden />
      </Link>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{session.title}</p>
        <p className="truncate text-[13px] text-white/60">
          {session.teamName} · {formatLongDay(session.date)} · {formatTime(session.startTime)}
        </p>
      </div>
      {children}
    </header>
  );
}

function SyncStatus({ offline, pending }: { offline: boolean; pending: number }) {
  if (offline || pending) {
    return (
      <span className="flex items-center gap-1.5 text-[13px] text-warn" role="status">
        <CloudOff className="size-4" aria-hidden /> {offline ? 'Offline — will sync' : 'Saving…'}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-[13px] text-white/60" role="status">
      <Check className="size-4" aria-hidden /> Saved
    </span>
  );
}

// ---------- Roll call ----------

function RollCall({ state, onMark, onStart, sync }: { state: Saved; onMark: (id: string, s: AttendanceStatus | null) => void; onStart: () => void; sync: React.ReactNode }) {
  const { session, rows } = state;
  const drills = state.run.blocks.filter((b) => b.kind === 'drill');
  const blocks = drills.length;
  const minutes = state.run.blocks.reduce((n, b) => n + b.planned, 0);
  const unmarked = rows.filter((r) => !r.status);
  return (
    <Shell>
      <TopBar session={session}>{rows.length > 0 && sync}</TopBar>
      <main className="mx-auto max-w-2xl px-4 pt-5 pb-32">
        {!rows.length ? (
          <>
            <h1 className="text-[22px] font-semibold">Ready to start</h1>
            {blocks > 0 && (
              <p className="tnum mt-1 text-white/70">
                {blocks} {blocks === 1 ? 'drill' : 'drills'}, {minutes} min planned.
              </p>
            )}
            <p className="mt-4 text-[14px] text-white/50">Taking attendance is optional. To use it, add players on the team’s Roster tab.</p>
          </>
        ) : (
          <>
            <h1 className="text-[22px] font-semibold">Roll call</h1>
            <p className="tnum mt-1 text-white/70">{attendanceSummary(rows)}</p>
            <p className="mt-1 mb-4 text-[14px] text-white/50">Optional: you can start without marking anyone, and fill it in later on the session page.</p>
            {state.canMark ? (
              <>
                <AttendanceList big rows={rows} onSet={onMark} />
                {unmarked.length > 0 && (
                  <button className="mt-4 w-full rounded-lg bg-white/10 px-4 py-3 font-medium" onClick={() => unmarked.forEach((r) => onMark(r.player.id, r.unavailable ? 'absent' : 'present'))}>
                    Mark everyone else here
                  </button>
                )}
              </>
            ) : (
              <AttendanceList big readOnly rows={rows} onSet={() => {}} />
            )}
          </>
        )}
      </main>
      <div className="fixed inset-x-0 bottom-0 border-t border-white/10 bg-paper/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto max-w-2xl">
          {blocks ? (
            <button className="w-full rounded-xl bg-brand-gradient px-4 py-4 text-[17px] font-bold uppercase tracking-[0.06em] text-brand-ink" onClick={() => { unlockAudio(); onStart(); }}>
              Start session
            </button>
          ) : (
            <div className="text-center">
              <p className="pb-3 text-white/70">This session has no drills yet, so there’s nothing to run.</p>
              <Link to={`/teams/${session.teamId}/sessions/${session.id}`} className="block w-full rounded-xl bg-brand-gradient px-4 py-4 text-[17px] font-bold uppercase tracking-[0.06em] text-brand-ink">
                Add drills to the plan
              </Link>
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

// ---------- Running ----------

function useNow(ms = 250) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}

/** Keeps the screen on while courtside mode is open (where the browser allows it). */
export function useWakeLock() {
  useEffect(() => {
    type Lock = { release: () => Promise<void> };
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<Lock> } };
    if (!nav.wakeLock) return;
    let lock: Lock | null = null;
    let live = true;
    const get = () => {
      if (document.visibilityState !== 'visible') return;
      nav.wakeLock!.request('screen').then(
        (l) => (live ? (lock = l) : void l.release()),
        () => {},
      );
    };
    get();
    document.addEventListener('visibilitychange', get);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', get);
      void lock?.release().catch(() => {});
    };
  }, []);
}

let audio: AudioContext | null = null;
/** Browsers only allow sound after a tap, so this runs on Start. */
function unlockAudio() {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    audio ??= new AC();
    void audio.resume();
  } catch {
    audio = null;
  }
}
function beep() {
  try {
    if (audio) {
      for (let i = 0; i < 3; i++) {
        const o = audio.createOscillator();
        const g = audio.createGain();
        const t = audio.currentTime + i * 0.35;
        o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
        o.connect(g).connect(audio.destination);
        o.start(t);
        o.stop(t + 0.3);
      }
    }
    navigator.vibrate?.([300, 150, 300, 150, 300]);
  } catch {
    /* No sound or vibration here. */
  }
}

function scheduledStart(s: SessionDetail) {
  const [y, m, d] = s.date.split('-').map(Number);
  const [hh, mm] = s.startTime.split(':').map(Number);
  return new Date(y!, m! - 1, d!, hh, mm).getTime();
}

function behindText(mins: number) {
  if (mins >= 1) return { text: `Running ${mins} min behind`, tone: 'text-warn' };
  if (mins <= -1) return { text: `${-mins} min ahead`, tone: 'text-ok' };
  return { text: 'On time', tone: 'text-white/70' };
}

function Running({ state, update, sync }: { state: Saved; update: (fn: (s: Saved) => Saved) => void; sync: React.ReactNode }) {
  const now = useNow();
  useWakeLock();
  const { run, session } = state;
  const [details, setDetails] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const block = run.blocks[run.index]!;
  const remaining = C.remainingMs(run, now);
  const over = remaining < 0;
  const target = C.targetEnd(run, scheduledStart(session), session.durationMinutes);
  const behind = behindText(C.minutesBehind(run, now, target));
  const upNext = run.blocks.slice(run.index + 1).find((b) => !b.skipped);
  const paused = run.pausedAt !== null;
  const active = run.blocks.filter((b) => !b.skipped);
  const position = active.findIndex((b) => b.id === block.id) + 1;
  // "Drill 3 of 9" counts drills only; breaks sit between them.
  const drills = active.filter((b) => b.kind !== 'break');
  const drillNo = drills.findIndex((b) => b.id === block.id) + 1;
  const isBreak = block.kind === 'break';

  // Beep once when this drill's time is up.
  const beeped = useRef<string | null>(null);
  useEffect(() => {
    const id = `${block.id}:${run.blockStartedAt}`;
    if (remaining <= 0 && !paused && beeped.current !== id) {
      beeped.current = id;
      // Don't beep for a drill that ran out while the screen was off for ages.
      if (remaining > -5000) beep();
    }
  }, [remaining, paused, block.id, run.blockStartedAt]);

  const act = useCallback((fn: (r: C.Run, now: number) => C.Run) => update((s) => ({ ...s, run: fn(s.run, Date.now()) })), [update]);

  // Laptop keys: right arrow / Enter for Next, left for Back, space to pause.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (adjusting || (e.target as HTMLElement).closest('input,textarea,button')) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') act(C.next);
      else if (e.key === 'ArrowLeft') act(C.back);
      else if (e.key === ' ') {
        e.preventDefault();
        act((r, n) => (r.pausedAt ? C.resume(r, n) : C.pause(r, n)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act, adjusting]);

  const hasDetails = block.items.some((i) => i.coachingPoints || i.description || i.equipment || i.notes || i.diagram);

  return (
    <Shell>
      <div className="flex min-h-dvh flex-col">
        <TopBar session={session}>
          <div className="hidden sm:block">{sync}</div>
          <FullScreenButton />
        </TopBar>
        <div className="flex items-center gap-3 border-b border-white/10 px-4 py-2 text-[14px]">
          <span className={`font-medium ${behind.tone}`}>{behind.text}</span>
          <span className="tnum text-white/60">Finishes {clockTime(C.projectedEnd(run, now))}</span>
          <span className="ml-auto sm:hidden">{sync}</span>
          <button className="ml-auto hidden items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 font-medium sm:flex" onClick={() => setAdjusting(true)}>
            <SlidersHorizontal className="size-4" aria-hidden /> Adjust
          </button>
        </div>

        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pt-4 pb-40">
          <p className="tnum text-[14px] font-medium tracking-wide text-white/60 uppercase">
            {isBreak ? `Before drill ${Math.min(drills.length, drills.findIndex((b) => b.id === upNext?.id) + 1 || drills.length)} of ${drills.length}` : `${block.section} · ${drillNo} of ${drills.length}`}
          </p>
          {isBreak && (
            <h1 className="mt-1 flex items-center gap-3 text-[32px] leading-tight font-semibold text-blue sm:text-[44px]">
              <GlassWater className="size-9" aria-hidden /> Drink break
            </h1>
          )}
          <div className="mt-1 space-y-1">
            {block.items.map((i) => (
              <h1 key={i.id} className={`leading-tight font-semibold ${block.items.length > 1 ? 'text-[26px] sm:text-[32px]' : 'text-[32px] sm:text-[44px]'}`}>
                {i.group && <span className="mr-2 text-white/60">{i.group}:</span>}
                {i.title}
                {block.items.length > 1 && i.minutes !== block.planned && <span className="tnum ml-2 text-[18px] font-normal text-white/60">{i.minutes} min</span>}
              </h1>
            ))}
          </div>

          <div className="my-6 text-center sm:my-8" aria-live="off">
            <p className={`tnum font-display text-[80px] leading-none font-black tracking-tight sm:text-[128px] ${over ? 'text-warn' : paused ? 'text-white/40' : ''}`}>{C.clock(remaining)}</p>
            <p className="mt-2 text-[15px] text-white/60">{paused ? 'Paused' : over ? 'Time’s up — over by' : `of ${block.minutes} min`}</p>
            <div className="mt-4 flex justify-center gap-2">
              <button className="flex items-center gap-1 rounded-lg bg-white/10 px-4 py-3 font-medium" onClick={() => update((s) => ({ ...s, run: C.setMinutes(s.run, block.id, block.minutes - 1) }))} aria-label="One minute less">
                <Minus className="size-4" aria-hidden /> 1 min
              </button>
              <button
                className="flex items-center gap-2 rounded-lg bg-white/10 px-5 py-3 font-medium"
                onClick={() => act((r, n) => (r.pausedAt ? C.resume(r, n) : C.pause(r, n)))}
              >
                {paused ? <Play className="size-4" aria-hidden /> : <Pause className="size-4" aria-hidden />}
                {paused ? 'Resume' : 'Pause'}
              </button>
              <button className="flex items-center gap-1 rounded-lg bg-white/10 px-4 py-3 font-medium" onClick={() => update((s) => ({ ...s, run: C.setMinutes(s.run, block.id, block.minutes + 1) }))} aria-label="One minute more">
                <Plus className="size-4" aria-hidden /> 1 min
              </button>
            </div>
          </div>

          {isBreak && <NextDrillPanel next={upNext ?? null} />}

          {hasDetails && (
            <button className="mb-3 self-start rounded-lg border border-white/20 px-4 py-2.5 font-medium" onClick={() => setDetails(!details)} aria-expanded={details}>
              {details ? 'Hide coaching points' : 'Coaching points and diagram'}
            </button>
          )}
          {details && (
            <div className="space-y-6 rounded-xl bg-white/5 p-4">
              {block.items.map((i) => (
                <div key={i.id}>
                  {block.items.length > 1 && <h2 className="mb-2 font-semibold">{i.group ? `${i.group}: ` : ''}{i.title}</h2>}
                  {i.coachingPoints && <Detail label="Coaching points" text={i.coachingPoints} big />}
                  {i.description && <Detail label="How it runs" text={i.description} />}
                  {i.equipment && <Detail label="Equipment" text={i.equipment} />}
                  {i.notes && <Detail label="Your note" text={i.notes} />}
                  {i.diagram && (
                    <div className="mt-3 rounded-lg bg-surface p-2 text-ink">
                      <DiagramView diagram={i.diagram} compact />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isBreak && (
            <div className="mt-6 border-t border-white/10 pt-4">
              <p className="text-[13px] font-medium tracking-wide text-white/50 uppercase">Up next</p>
              {upNext ? (
                <p className={`mt-1 text-[18px] ${upNext.kind === 'break' ? 'text-blue' : ''}`}>
                  {C.stepLabel(upNext)}
                  <span className="tnum ml-2 text-white/60">{upNext.minutes} min</span>
                </p>
              ) : (
                <p className="mt-1 text-[18px] text-white/70">That’s the last one.</p>
              )}
            </div>
          )}
        </main>

        <div className="fixed inset-x-0 bottom-0 border-t border-white/10 bg-paper/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-3xl gap-2">
            <button className="rounded-xl bg-white/10 px-4 py-4 disabled:opacity-40" disabled={position <= 1} onClick={() => act(C.back)} aria-label="Previous drill">
              <ChevronLeft className="size-6" aria-hidden />
            </button>
            <button className="rounded-xl bg-white/10 px-4 py-4 sm:hidden" onClick={() => setAdjusting(true)} aria-label="Adjust timing">
              <SlidersHorizontal className="size-6" aria-hidden />
            </button>
            <button className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-gradient px-4 py-4 text-[18px] font-bold uppercase tracking-[0.06em] text-brand-ink" onClick={() => act(C.next)}>
              {upNext ? 'Next' : 'Finish'} <ChevronRight className="size-6" aria-hidden />
            </button>
          </div>
        </div>
      </div>
      {adjusting && <Adjust state={state} update={update} target={target} onClose={() => setAdjusting(false)} />}
    </Shell>
  );
}

/** During a drink break: the next drill in full, so the coach can explain it and set up. */
function NextDrillPanel({ next }: { next: C.RunBlock | null }) {
  if (!next) return <p className="mt-4 text-[18px] text-white/70">That’s the last drill done.</p>;
  return (
    <section className="mt-2 rounded-xl border border-white/15 bg-white/5 p-4" aria-label="Next drill">
      <p className="text-[13px] font-medium tracking-wide text-white/50 uppercase">
        Next: {next.section} · {next.minutes} min
      </p>
      <div className="mt-1 space-y-6">
        {next.items.map((i) => (
          <div key={i.id}>
            <h2 className="text-[24px] leading-tight sm:text-[28px] sm:text-[30px]">
              {i.group && <span className="mr-2 text-white/60">{i.group}:</span>}
              {i.title}
            </h2>
            <div className="mt-3">
              {i.equipment && <Detail label="Set up" text={i.equipment} big />}
              {i.coachingPoints && <Detail label="Coaching points" text={i.coachingPoints} big />}
              {i.description && <Detail label="How it runs" text={i.description} />}
              {i.notes && <Detail label="Your note" text={i.notes} />}
            </div>
            {i.diagram && (
              <div className="mt-3 rounded-lg bg-surface p-2 text-ink">
                <DiagramView diagram={i.diagram} compact />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Detail({ label, text, big }: { label: string; text: string; big?: boolean }) {
  return (
    <div className="mb-3">
      <p className="text-[13px] font-medium tracking-wide text-white/50 uppercase">{label}</p>
      <p className={`whitespace-pre-line ${big ? 'text-[18px]' : 'text-white/85'}`}>{text}</p>
    </div>
  );
}

function clockTime(ms: number) {
  if (uses24Hour()) return new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(ms);
  return new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' }).format(ms).replace(' ', '').toLowerCase();
}

export function FullScreenButton() {
  const can = typeof document !== 'undefined' && !!document.documentElement.requestFullscreen;
  if (!can) return null;
  return (
    <button
      className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
      aria-label="Full screen"
      onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => {})}
    >
      <Maximize className="size-5" aria-hidden />
    </button>
  );
}

// ---------- Adjust the rest of the session ----------

function Adjust({ state, update, target, onClose }: { state: Saved; update: (fn: (s: Saved) => Saved) => void; target: number; onClose: () => void }) {
  const now = useNow(1000);
  const { run } = state;
  const rest = run.blocks.slice(run.index + 1);
  const behind = behindText(C.minutesBehind(run, now, target));
  const set = (fn: (r: C.Run) => C.Run) => update((s) => ({ ...s, run: fn(s.run) }));
  const late = C.minutesBehind(run, now, target) >= 1;

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center bg-black/60 sm:items-center" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Adjust the rest of the session" className="max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl bg-surface p-4 sm:max-w-lg sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center">
          <h2 className="flex-1 text-[18px] font-semibold">Adjust the rest</h2>
          <button className="rounded-md p-1.5 text-white/70 hover:bg-white/10" onClick={onClose} aria-label="Close">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <p className={`mb-4 ${behind.tone}`}>{behind.text}. Finishes {clockTime(C.projectedEnd(run, now))}, aiming for {clockTime(target)}.</p>
        {!rest.length ? (
          <p className="text-white/70">This is the last drill.</p>
        ) : (
          <>
            {late && (
              <button className="mb-4 w-full rounded-lg bg-brand-gradient px-4 py-3 font-semibold text-brand-ink" onClick={() => set((r) => C.fitToTime(r, Date.now(), target))}>
                Fit the rest to time
              </button>
            )}
            <ul className="divide-y divide-white/10">
              {rest.map((b) => (
                <li key={b.id} className={`flex items-center gap-2 py-3 ${b.skipped ? 'opacity-50' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className={`truncate font-medium ${b.skipped ? 'line-through' : ''}`}>{C.stepLabel(b)}</p>
                    <p className="tnum text-[13px] text-white/50">
                      {b.kind === 'break' ? 'Between parts' : b.section}
                      {b.minutes !== b.planned && !b.skipped && ` · planned ${b.planned} min`}
                    </p>
                  </div>
                  {!b.skipped && (
                    <div className="flex items-center gap-1">
                      <button className="rounded-md bg-white/10 p-2" onClick={() => set((r) => C.setMinutes(r, b.id, b.minutes - 1))} aria-label={`One minute less for ${C.stepLabel(b)}`}>
                        <Minus className="size-4" aria-hidden />
                      </button>
                      <span className="tnum w-12 text-center">{b.minutes} min</span>
                      <button className="rounded-md bg-white/10 p-2" onClick={() => set((r) => C.setMinutes(r, b.id, b.minutes + 1))} aria-label={`One minute more for ${C.stepLabel(b)}`}>
                        <Plus className="size-4" aria-hidden />
                      </button>
                    </div>
                  )}
                  <button className="w-16 rounded-md bg-white/10 px-2 py-2 text-[14px]" onClick={() => set((r) => C.toggleSkip(r, b.id))}>
                    {b.skipped ? 'Undo' : 'Skip'}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        <button className="mt-4 w-full rounded-lg bg-white/10 px-4 py-3 font-medium" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

// ---------- Finished ----------

function Finished({ state, update, sync, offline }: { state: Saved; update: (fn: (s: Saved) => Saved) => void; sync: React.ReactNode; offline: boolean }) {
  const navigate = useNavigate();
  const { run, session, rows } = state;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const changed = C.hasChanges(run) && !state.planSaved;
  const total = Math.round(((run.finishedAt ?? 0) - (run.startedAt ?? 0)) / 60_000);
  const lines = useMemo(() => run.blocks, [run.blocks]);
  const base = `/teams/${session.teamId}/sessions/${session.id}`;

  const save = async (latest = false) => {
    setSaving(true);
    setError(null);
    try {
      const from = latest ? await api.get<SessionDetail>(base) : session;
      const next = await api.put<SessionDetail>(`${base}/plan`, { version: from.version, plan: C.applyToPlan(from.plan, run) });
      update((s) => ({ ...s, session: next, planSaved: true }));
      setConflict(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setConflict(true);
      else setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const close = () => {
    if (Object.keys(state.pending).length) return; // Keep it until attendance has synced.
    forget(session.id);
    navigate(base);
  };

  return (
    <Shell>
      <TopBar session={session}>{sync}</TopBar>
      <main className="mx-auto max-w-2xl px-4 py-6">
        <h1 className="text-[26px] font-semibold">Session done</h1>
        <p className="tnum mt-1 text-white/70">
          Ran {total} min of {session.durationMinutes} planned.{rows.length ? ` ${attendanceSummary(rows)}.` : ''}
        </p>

        <ul className="mt-5 divide-y divide-white/10 rounded-xl bg-white/5">
          {lines.map((b) => (
            <li key={b.id} className={`flex items-center gap-3 px-4 py-2.5 ${b.skipped ? 'text-white/40' : ''}`}>
              <span className={`min-w-0 flex-1 truncate ${b.skipped ? 'line-through' : ''}`}>{C.stepLabel(b)}</span>
              <span className="tnum shrink-0 text-[14px] text-white/60">
                {b.skipped ? 'Skipped' : `${run.actual[b.id] ? run.actual[b.id] : 'Under 1'} of ${b.planned} min`}
              </span>
            </li>
          ))}
        </ul>

        {changed && (
          <div className="mt-6 rounded-xl border border-white/15 p-4">
            <p className="font-medium">You changed the timing during the session.</p>
            <p className="mt-1 text-white/70">Save the new times to the plan, and remove skipped drills? Useful if you’ll copy this session again.</p>
            {conflict && <p className="mt-3 text-warn">Someone changed the plan while you were courtside. You can apply your timing changes to their latest version.</p>}
            {error && <p className="mt-3 text-danger">{error}</p>}
            {offline && <p className="mt-3 text-warn">You’re offline. Save this once you’re back online.</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              <button className="rounded-lg bg-brand-gradient px-4 py-3 font-semibold text-brand-ink disabled:opacity-50" disabled={saving || offline} onClick={() => void save(conflict)}>
                {saving ? 'Saving…' : conflict ? 'Apply to the latest plan' : 'Save to the plan'}
              </button>
              <button className="rounded-lg bg-white/10 px-4 py-3 font-medium" onClick={() => update((s) => ({ ...s, planSaved: true }))}>
                Keep the plan as it was
              </button>
            </div>
          </div>
        )}
        {state.planSaved && C.hasChanges(run) && <p className="mt-6 text-white/70">Plan sorted.</p>}

        <div className="mt-6 flex flex-wrap gap-2">
          <button className="rounded-lg bg-white/10 px-4 py-3 font-medium" onClick={() => update((s) => ({ ...s, run: { ...s.run, finishedAt: null, index: s.run.index, blockStartedAt: Date.now() } }))}>
            Back to the last drill
          </button>
          <button className="rounded-lg border-2 border-white px-4 py-3 font-bold uppercase tracking-wide text-white disabled:opacity-50" disabled={Object.keys(state.pending).length > 0} onClick={close}>
            Close courtside mode
          </button>
        </div>
        {Object.keys(state.pending).length > 0 && <p className="mt-2 text-[14px] text-warn">Attendance hasn’t synced yet. Keep this open until you’re back online.</p>}
      </main>
    </Shell>
  );
}
